import { Button, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import {
  FaBookmark,
  FaDesktop,
  FaEye,
  FaPencil,
  FaPlus,
} from "react-icons/fa6";
import { api } from "@/api";
import { Office } from "@/api/offices";

interface OfficeWithStats extends Office {
  view_count?: number;
  like_count?: number;
}
import useErrorHandling from "@/hooks/useErrorHandling";
import { AdminPermissions } from "@/api/auth/auth.ts";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { Link } from "react-router-dom";
import { useTour } from "@/hooks/useTour";

interface TabLocationInterface {
  id: number;
  display_name: string;
}

type TabLocation = TabLocationInterface | null;

export function OfficesPage() {
  const [rawOffices, setRawOffices] = useState<Office[] | null>(null);
  const [offices, setOffices] = useState<Office[] | null>(null);
  const [tabs, setTabs] = useState<TabLocation[] | null>(null);
  const [activeTab, setActiveTab] = useState<string | number>("all");

  const cachedLocations = useAppSelector((state) => state.handbooks.location);

  const handleError = useErrorHandling();

  const createOfficeModalDisclosure = useDisclosure();
  const editOfficeModalDisclosure = useDisclosure();
  const fullscreenDisclosure = useDisclosure();

  useTour(4);

  useEffect(() => {
    api.offices
      .list()
      .then(async (response) => {
        setRawOffices(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    createOfficeModalDisclosure.isOpen,
    editOfficeModalDisclosure.isOpen,
    handleError,
  ]);

  useEffect(() => {
    const locations: TabLocation[] = [];

    rawOffices?.forEach((office) => {
      if (office.location) {
        if (!locations.find((loc) => loc?.id == office.location))
          locations.push({
            id: office.location,
            display_name:
              cachedLocations.find((l) => l.id == office.location)
                ?.display_name || "Локация " + office.location,
          });
      }
    });

    setTabs(locations);
  }, [cachedLocations, rawOffices]);

  useEffect(() => {
    if (activeTab === "all") return setOffices(rawOffices);
    const filteredOffices: Office[] = [
      ...(rawOffices?.filter((office) => office.location == activeTab) || []),
    ];
    return setOffices(filteredOffices);
  }, [rawOffices, activeTab]);

  return (
    <>
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="offices-title">
                Офисы
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Link to="/dashboard/offices/public">
                <Button
                  startContent={<FaEye />}
                  color="primary"
                  variant="bordered"
                  className="max-w-fit">
                  Офисы на маркетплейсе
                </Button>
              </Link>
              <Link to="/dashboard/offices/create">
                <Button
                  startContent={<FaPlus />}
                  color="primary"
                  variant="solid"
                  className="max-w-fit"
                  id="create-office-button">
                  Создать офис
                </Button>
              </Link>
            </div>
          </div>
          <div className="flex flex-row gap-2 mb-4">
            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activeTab}
              onSelectionChange={setActiveTab}>
              <Tab key="all" title="Все локации" />
              {tabs?.map((tab) => (
                <Tab key={tab?.id} title={tab?.display_name}></Tab>
              ))}
            </Tabs>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <div id="offices-table">
              <TableBuilder
                fullscreenDisclosure={fullscreenDisclosure}
                displayOptions={false}
                removeWrapper
                columns={[
                  {
                    key: "display_name",
                    label: "Название",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    key: "area",
                    label: "Площадь",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    key: "price",
                    label: "Цена",
                    type: ColumnType.Number,
                    permissions: [AdminPermissions.ViewLandlordReports],
                    sortable: true,
                  },
                  {
                    key: "floor",
                    label: "Этаж",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    key: "room_count",
                    label: "Комнаты",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    key: "workplace_count",
                    label: "Рабочие места",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    key: "office_type",
                    label: "Тип офиса",
                    type: ColumnType.OfficeType,
                    sortable: true,
                  },
                  {
                    key: "location",
                    label: "Локация",
                    type: ColumnType.Location,
                    sortable: true,
                  },
                  {
                    key: "public",
                    label: "Опубликован",
                    type: ColumnType.Boolean,
                    sortable: true,
                  },
                  {
                    key: "stats",
                    label: "Статистика",
                    type: ColumnType.Custom,
                    render: (_, row: OfficeWithStats) => (
                      <div className="flex flex-row items-center gap-4 text-sm">
                        <div className="flex flex-row items-center gap-2">
                          <span>
                            <FaEye />
                          </span>
                          <span>{row.view_count || 0}</span>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                          <span>
                            <FaBookmark />
                          </span>
                          <span>{row.like_count || 0}</span>
                        </div>
                      </div>
                    ),
                    sortable: false,
                  },
                  {
                    key: "actions",
                    label: "Действия",
                    type: ColumnType.Custom,
                    render: (_, row: Office) => (
                      <div className="flex gap-2">
                        <Link to={`/dashboard/offices/${row.id}/edit`}>
                          <Button isIconOnly size="sm" variant="bordered">
                            <FaPencil />
                          </Button>
                        </Link>
                        {/* todo: это помойка! нужно сделать enum */}
                        {row.office_type == 7 && (
                          <Link to={`/dashboard/offices/${row.id}/workplaces`}>
                            <Button isIconOnly size="sm" variant="bordered">
                              <FaDesktop />
                            </Button>
                          </Link>
                        )}
                      </div>
                    ),
                  },
                ]}
                data={offices || []}
                rowsPerPage={10}
                defaultSortDescriptor={{
                  column: "display_name",
                  direction: "ascending",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
